import omit from 'lodash/omit';
import {
  CANADA_STATES,
  CardType,
  USA_STATES,
  CONDO_BOOKING_GUEST_INFO_LABEL,
  CONDO_BOOKING_CARD_INFO_LABEL,
} from 'src/constants/index';
import {
  ICardBooking,
  ICondoCardBooking,
  ICondoGuestBooking,
  ICountryState,
  IErrorField,
} from 'src/common-types/index';
import { ICondoReviewBookState, IReviewBookState } from 'src/store/slices/index';
import { checkForEmptyCharacter, insertSpacesInNumber, insertSpacesInPhone } from './common';

const THREE = 3;
const FOUR = 4;

export const detectCardType = (cardNumber: string): string => {
  const re: { [key: string]: RegExp } = {
    [CardType.Electron]: /^(4026|417500|4405|4508|4844|4913|4917)\d+$/,
    [CardType.Maestro]: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
    [CardType.Dankort]: /^(5019)\d+$/,
    [CardType.Interpayment]: /^(636)\d+$/,
    [CardType.Unionpay]: /^(62|88)\d+$/,
    [CardType.Visa]: /^4[0-9]{12}(?:[0-9]{3})?$/,
    [CardType.Mastercard]: /^5[1-5][0-9]{14}$/,
    [CardType.Amex]: /^3[47][0-9]{13}$/,
    [CardType.Diners]: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    [CardType.Discover]: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    [CardType.Jcb]: /^(?:2131|1800|35\d{3})\d{11}$/,
  };

  for (const key in re) {
    if (re[key].test(cardNumber)) {
      return key;
    }
  }
};

export const getErrorMessage = (errorArray: IErrorField[], name: string): string => {
  if (!errorArray) {
    return;
  }

  const errorField = errorArray.find((field) => field.field === name);

  if (errorField) {
    return errorField.errorMessage;
  } else {
    return;
  }
};

export const setValidator = (
  errorsField: IErrorField[],
  name: string,
  checkError: boolean,
): Promise<void> => {
  if (getErrorMessage(errorsField, name) === undefined || !checkError) {
    return Promise.resolve();
  } else {
    return Promise.reject(new Error(getErrorMessage(errorsField, name)));
  }
};

export const setNumberPhone = (data: string): string => {
  const THREE = 3;
  const value = checkForEmptyCharacter(data);
  const matchPhone = insertSpacesInPhone(value);

  if (value.length > THREE && matchPhone !== null) {
    return matchPhone.join('-').trim();
  } else if (!Number(value)) {
    return '';
  } else {
    return value;
  }
};

export const updateCondoGuest = (
  guestObject: ICondoGuestBooking | ICardBooking | ICondoCardBooking,
  value: { [key: string]: string | number | boolean },
  isGuest = true,
): ICondoGuestBooking | ICardBooking | ICondoCardBooking => {
  const newGuest = {
    ...guestObject,
    ...value,
  };

  if (isGuest) {
    localStorage.setItem(
      CONDO_BOOKING_GUEST_INFO_LABEL,
      JSON.stringify(omit(newGuest, ['zipCode', 'stateCode', 'country', 'countryCode'])),
    );
  } else {
    localStorage.setItem(
      CONDO_BOOKING_CARD_INFO_LABEL,
      JSON.stringify(
        omit(newGuest, [
          'country',
          'zipCode',
          'stateCode',
          'cvv',
          'cardNumber',
          'cardType',
          'expireDate',
          'holderName',
        ]),
      ),
    );
  }

  return newGuest;
};

export const showCardNumber = (data: ICondoReviewBookState | IReviewBookState): string => {
  const { card } = data;

  if (!card.cardNumber) {
    return;
  }
  const value = checkForEmptyCharacter(card.cardNumber);

  return insertSpacesInNumber(value).join(' ').trim();
};

export const getHiddenFields = (codeCVV: string): string => {
  if (codeCVV.length === THREE) {
    return '***';
  } else if (codeCVV.length === FOUR) {
    return '****';
  }
};

export const getStates = (data: ICondoReviewBookState | IReviewBookState): ICountryState[] => {
  const { card } = data;

  if (card?.country === 'US') {
    return USA_STATES;
  }

  if (card?.country === 'CA') {
    return CANADA_STATES;
  }

  return [];
};

export const removingDashFromString = (data: string): string => {
  return data.split('-').join('');
};
